import _ from 'lodash';

import { GUIDED_EDIT_MODE } from 'app/beta-editor/constants/EditModes';
import { NEW_ZAP_ID } from 'app/beta-editor/constants';
import { nodeTemplatesFromQuery } from 'app/beta-editor/utils/TemplateUtils';
import { sortNodes } from 'app/beta-editor/utils/NodeTrees';
import {
  cleanObjectOfEmptyValues,
  exists,
  replaceParams,
  superHumanize,
  unflatten,
} from 'app/common/CommonUtils';
import { createFlow, stepsFromNodes } from 'app/beta-editor/utils/FlowUtils';

const areFlowSteps = steps => {
  const nodeTypes = steps
    .filter(step => step.type === 'node')
    .map(step => step.type_of);
  return (
    nodeTypes.length > 2 ||
    nodeTypes.indexOf('read') < 0 ||
    nodeTypes.indexOf('write') < 0
  );
};

export const isFlow = sharedZap => {
  let steps = [];
  if (_.isArray(sharedZap)) {
    steps = sharedZap;
  } else if (sharedZap.steps) {
    steps = sharedZap.steps;
  }
  return areFlowSteps(steps);
};

export const validateParams = sharedZap => {
  const errors = [];
  const steps = sharedZap.steps || [];
  const idToStep = {};

  const nodeSteps = steps.filter(step => step.type === 'node');

  steps.forEach((step, i) => {
    const { params = {} } = step;
    const stepId = step.id;

    if (step.type !== 'node') {
      return;
    }

    Object.keys(params).forEach(paramKey => {
      replaceParams(params[paramKey], (id, key) => {
        if (id) {
          if (!idToStep[id]) {
            // Ignore if we only have 2 nodes.
            if (nodeSteps.length > 2) {
              errors.push({
                stepIndex: i,
                param: {
                  id,
                  key,
                },
              });
            }
          }
        }
      });
    });

    // Do this last, because a step's params can only reference previous step.
    if (stepId) {
      idToStep[stepId] = step;
    }
  });

  return errors;
};

const mergeDeprecatedParams = ({
  params,
  customTemplate,
  typeOf,
  shouldCustomize = true,
}) => {
  params = params || {};
  customTemplate = customTemplate || {};

  if (typeOf === 'filter' || !shouldCustomize) {
    return params;
  }
  if (typeOf === 'read' && customTemplate.trigger_data) {
    return {
      ...params,
      ...customTemplate.trigger_data,
    };
  }
  if (typeOf === 'write' && customTemplate.action_data) {
    return {
      ...params,
      ...customTemplate.action_data,
    };
  }
  return params;
};

// Take some URL query parameters and produce a "custom template" which is some
// on-the-fly overrides for a zap.
export const convertDeprecatedParams = query => {
  if (!query) {
    return null;
  }
  const flatValues = Object.keys(query).reduce((result, key) => {
    if (
      _.startsWith(key, 'trigger_data__') ||
      _.startsWith(key, 'action_data__')
    ) {
      result[key] = query[key];
    }
    return result;
  }, {});
  const unflatValues = unflatten(flatValues);
  if (!unflatValues.trigger_data && !unflatValues.action_data) {
    return null;
  }
  return cleanObjectOfEmptyValues({
    trigger_data: unflatValues.trigger_data,
    action_data: unflatValues.action_data,
  });
};

export const selectNodeStepsWithIndexes = sharedZap => {
  return _.get(sharedZap, 'steps', [])
    .map((step, index) => ({
      step,
      index,
    }))
    .filter(({ step }) => step.type === 'node');
};

const selectNodeSteps = sharedZap => {
  return _.get(sharedZap, 'steps', []).filter(step => step.type === 'node');
};

// Create some template nodes from a shared zap, where params point to step
// indexes instead of actual node IDs.
export const nodeTemplates = (sharedZap, { query, customTemplate } = {}) => {
  if (query && !customTemplate) {
    customTemplate = convertDeprecatedParams(query);
  }

  const nodeSteps = selectNodeSteps(sharedZap);

  // Build a map from step id (a local id for the step) to node index.
  const stepIdToNodeIndex = _.fromPairs(
    _.compact(
      nodeSteps.map((step, i) => {
        return step.id && [step.id, i];
      })
    )
  );

  // Silliness because of action overrides.
  let didFindFirstAction = false;

  const templates =
    nodeTemplatesFromQuery(query, { allowSparseNodes: true }) || [];
  const paramOverrides = templates.map(({ params }) => params);
  const metaOverrides = templates.map(({ meta }) => meta);

  return nodeSteps.map((step, nodeIndex) => {
    const shouldCustomize = nodeIndex === 0 || !didFindFirstAction;

    const fullParams = {
      ...mergeDeprecatedParams({
        params: step.params,
        customTemplate,
        typeOf: step.type_of,
        shouldCustomize,
      }),
      ...paramOverrides[nodeIndex],
    };

    if (nodeIndex > 0 && step.type_of !== 'filter') {
      didFindFirstAction = true;
    }
    const cleanParams = cleanObjectOfEmptyValues(fullParams || {});
    const params = replaceParams(
      cleanParams,
      (id, key) => {
        // If there's no id, assume this param points to the first node (trigger).
        if (!id) {
          return `{{0__${key}}}`;
        }
        if (id in stepIdToNodeIndex) {
          // Param now points to node index.
          return `{{${stepIdToNodeIndex[id]}__${key}}}`;
        }
        // If there are only two steps, assume bad node IDs point to the trigger,
        // since that's the only valid node ID.
        if (nodeSteps.length === 2) {
          return `{{0__${key}}}`;
        }
        // Crap, you should have validated for this. Hmmm, what to do. Put a
        // human readable marker there.
        return `(Insert field for ${superHumanize(key)})`;
      },
      { isFilter: step.type_of === 'filter' }
    );

    return {
      type_of: step.type_of,
      selected_api: step.selected_api,
      action: step.action,
      params,
      meta: metaOverrides[nodeIndex],
    };
  });
};

export const stepIndexForNodeIndex = (sharedZap = {}, nodeIndex) => {
  return _.transform(
    sharedZap.steps || [],
    (result, step, index) => {
      if (step.type === 'node') {
        result.lastNodeIndex++;
        if (result.lastNodeIndex === nodeIndex) {
          result.stepIndex = index;
          return false;
        }
      }
      // continue
      return undefined;
    },
    { lastNodeIndex: -1, stepIndex: -1 }
  ).stepIndex;
};

const paramOverridesForStepIndex = (sharedZap = {}, stepIndex = -1) => {
  const steps = sharedZap.steps || [];
  return _.get(steps, [stepIndex, 'overrides'], {});
};

const paramsForStepIndex = (sharedZap = {}, stepIndex = -1) => {
  const steps = sharedZap.steps || [];
  return _.get(steps, [stepIndex, 'params'], {});
};

// Customize a field with shared zap overrides.
const applyOverrideToField = (override, field) => {
  field = { ...field };
  if (override.label) {
    field.label = override.label;
  }
  if (override.help_text_html || override.help_text) {
    field.help_text_html = override.help_text_html || override.help_text;
    field.hasOverriddenHelpText = true;
  }
  if (override.placeholder) {
    field.placeholder = override.placeholder;
  }
  if (override.force_required) {
    field.required = true;
  }
  if (field.itemFields) {
    field.itemFields = field.itemFields.map(itemField =>
      applyOverrideToField(itemField)
    );
  }
  return field;
};

// Customize a set of fields with a shared, simple zap step.
export const applyOverridesToFieldsForSimpleZap = (
  sharedZap,
  stepIndex,
  fields
) => {
  const overrides = paramOverridesForStepIndex(sharedZap, stepIndex);
  const params = paramsForStepIndex(sharedZap, stepIndex);

  const getFieldConfig = (field, result) => {
    if (field.isGroup && field.fields && field.fields.length > 0) {
      field.fields.forEach(i => getFieldConfig(i, result));

      return result;
    } else {
      if (overrides[field.key]) {
        const newField = applyOverrideToField(overrides[field.key], field);

        if (
          overrides[field.key].visibility === 'whitelist' &&
          (newField.prefill ||
            (newField.choices && !_.isEmpty(newField.choices)))
        ) {
          result.visible.push(newField);
        }
      } else if (
        field.required &&
        !params[field.key] &&
        !field.default &&
        (field.prefill || (field.choices && !_.isEmpty(field.choices)))
      ) {
        result.visible.push(field);
      }

      return result;
    }
  };

  const fieldsConfig = fields.reduce(
    (result, field) => getFieldConfig(field, result),
    {
      visible: [],
      hidden: [],
    }
  );

  return fieldsConfig;
};

// Customize a set of fields with a shared zap step.
export const applyOverridesToFields = (sharedZap, stepIndex, fields) => {
  const hasSomeRequiredCustomFields = fields.some(
    field => field.custom_field && field.required
  );

  const hasSomeNonPrefillNonCustom = fields.some(
    field => !exists(field.prefill) && !field.custom_field
  );

  const overrides = paramOverridesForStepIndex(sharedZap, stepIndex);
  const params = paramsForStepIndex(sharedZap, stepIndex);

  const getFieldConfig = (field, result) => {
    if (field.isGroup && field.fields && field.fields.length > 0) {
      field.fields.forEach(i => getFieldConfig(i, result));

      return result;
    } else {
      if (overrides[field.key]) {
        if (overrides[field.key].visibility !== 'blacklist') {
          const newField = applyOverrideToField(overrides[field.key], field);
          if (overrides[field.key].visibility === 'whitelist') {
            result.visible.push(newField);
          } else {
            result.hidden.push(newField);
          }
        }
      } else if (field.required && !params[field.key] && !field.default) {
        result.visible.push(field);
      } else if (
        field.custom_field &&
        (field.required ||
          (!hasSomeRequiredCustomFields && !hasSomeNonPrefillNonCustom))
      ) {
        result.visible.push(field);
      } else {
        result.hidden.push(field);
      }

      return result;
    }
  };

  const fieldsConfig = fields.reduce(
    (result, field) => getFieldConfig(field, result),
    {
      visible: [],
      hidden: [],
    }
  );

  return fieldsConfig;
};

// Make a flow object from a shared zap. Useful for shared zap start pages,
// before the flow is actually created on the server.
export const toFlow = (sharedZap, id = NEW_ZAP_ID) => {
  const nodesArray = nodeTemplates(sharedZap);
  const nodes = _.fromPairs(
    nodesArray.map((node, index) => {
      const nodeId = index === 0 ? id : `${id}-${index}`;
      let parentId = null;
      if (index === 1) {
        parentId = id;
      } else if (index > 1) {
        parentId = `${id}-${index - 1}`;
      }
      const title = index === 0 ? sharedZap.title : undefined;
      return [
        nodeId,
        {
          ...node,
          id: nodeId,
          parent_id: parentId,
          title,
        },
      ];
    })
  );

  nodes[id].meta = {
    ...nodes[id].meta,
    edit_mode: GUIDED_EDIT_MODE,
  };

  const nodeIds = sortNodes(nodes).map(node => node.id);

  const flow = {
    id,
    cid: id,
    nodes,
    nodeIds,
    folderIds: [0],
    title: nodes[nodeIds[0]].title,
    steps: stepsFromNodes(_.values(nodes)),
  };
  return createFlow(flow);
};

const getSharedZapStatus = sharedZap => {
  return _.get(sharedZap, ['status']);
};

const sharedZapHasStatus = (status, sharedZap) => {
  return !!status && getSharedZapStatus(sharedZap) === status;
};

export const isSharedZapCommunity = _.partial(sharedZapHasStatus, 'community');

export const generateDefaultSharedZapTitle = zapSummary => {
  const { zapNodeDetails } = zapSummary;
  const sortedAppNames = _.sortBy(
    Object.values(zapNodeDetails),
    node => node.stepNumber
  ).map(node => node.app);
  const appToApp = `${sortedAppNames[0]} → ${sortedAppNames[1]}`;
  const defaultTitle =
    sortedAppNames.length <= 2
      ? appToApp
      : sortedAppNames.length === 3
      ? `${appToApp} & ${sortedAppNames[2]}`
      : `${appToApp}, ${sortedAppNames[2]} & more!`;
  return _.truncate(defaultTitle, { length: 100 });
};

const builtInActionOverride = {
  storage: () => 'use storage for my data',
  digest: action =>
    ({
      'Append Entry and Schedule Digest': 'add item to digest',
      'Release Existing Digest': 'release a digest',
      'Find Digest': 'find a digest',
    }[action]),
  filter: () => 'filter my data',
  delay: () => 'delay my Zap',
  formatter: () => 'format my data',
  webhook: action =>
    ({
      PUT: 'send a PUT request',
      POST: 'send a POST request',
      GET: 'send a GET request',
      'Custom Request': 'send a custom request',
    }[action]),
};

const getBuiltInActionOverride = ({ slug, action }) => {
  const defaultAction = () => action.toLowerCase();
  const defaultFunction = builtInActionOverride[slug] || defaultAction;
  return defaultFunction(action) || defaultAction();
};

const builtInTriggerOverride = {
  code: triggerAction =>
    ({
      'Run Javascript': 'Anytime Javascript is run',
      'Run Javascript (deprecated)': 'Anytime Javascript is run',
      'Run Python': 'Anytime Python is run',
    }[triggerAction]),
  weather: triggerAction =>
    ({
      "Today's Forecast": 'Anytime today‘s forecast is ready',
      'Will it Rain Today?': 'Anytime rain is in the forecast',
    }[triggerAction]),
  schedule: triggerAction => triggerAction,
  rss: triggerAction =>
    ({
      'New Items in Multiple Feeds': 'Anytime one or more items is created',
      'New Item in Feed': 'Anytime a new item is created',
    }[triggerAction]),
  webhook: triggerAction =>
    ({
      'Retrieve Poll': 'Anytime a poll request finds a new item',
      'Catch Raw Hook': 'Anytime a raw hook is caught',
      'Catch Hook': 'Anytime a hook is caught',
    }[triggerAction]),
};

const getBuiltInTriggerOverride = ({ slug, action, noun }) => {
  const defaultTrigger = () => `Anytime ${noun} is created`;
  const defaultFunction = builtInTriggerOverride[slug] || defaultTrigger;
  return defaultFunction(action) || defaultTrigger();
};

export const generateDefaultSharedZapDescription = zapSummary => {
  const { zapNodeDetails, id } = zapSummary;
  const defaultStep = { noun: '', slug: '', action: '', app: '' };
  const triggerStep = zapNodeDetails[id] || defaultStep;
  const maybeBuiltInOverrideTrigger = getBuiltInTriggerOverride(triggerStep);
  const zapActions = _.omit(zapNodeDetails, id);
  const sortedActionSteps = _.sortBy(
    Object.values(zapActions),
    actionStep => actionStep.stepNumber
  );
  const eventsWithOverride = sortedActionSteps.map(
    (actionStep = defaultStep) => {
      const maybeBuiltInOverrideAction = getBuiltInActionOverride(actionStep);
      return `${maybeBuiltInOverrideAction} in ${actionStep.app}`;
    }
  );
  const descriptionStart = `${maybeBuiltInOverrideTrigger} in ${triggerStep.app}`;
  if (eventsWithOverride.length === 1) {
    return `${descriptionStart}, ${eventsWithOverride.join('')}.`;
  } else if (eventsWithOverride.length === 2) {
    return `${descriptionStart}, ${eventsWithOverride.join(' and ')}.`;
  } else {
    const firstTwoEvents = eventsWithOverride.slice(0, 2);
    return `${descriptionStart}, ${firstTwoEvents.join(', ')}, etc.`;
  }
};
